import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const STATE_TABS = [
  { id: '', label: 'All non-active' },
  { id: 'dormant', label: 'Dormant' },
  { id: 'graveyard', label: 'Graveyard' },
  { id: 'archived', label: 'Archived' },
];

function stateBadge(state) {
  const styles = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    dormant: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    graveyard: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[state] || styles.archived}`}>
      {state || 'unknown'}
    </span>
  );
}

function fmtMc(v) {
  const n = Number(v) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  if (n > 0) return `$${n.toFixed(0)}`;
  return '—';
}

export default function Graveyard() {
  const [tab, setTab] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [evalResult, setEvalResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/tokens/lifecycle/list', { params: { state: tab || undefined, limit: 100 } }),
        api.get('/tokens/lifecycle/stats'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data.stats || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const runEval = async (dryRun = true) => {
    setEvalResult(null);
    try {
      const res = await api.post(`/tokens/lifecycle/evaluate?dryRun=${dryRun ? '1' : '0'}`);
      setEvalResult(res.data);
      if (!dryRun) load();
    } catch (err) {
      setEvalResult({ error: err.response?.data?.error || 'Evaluation failed' });
    }
  };

  const act = async (action, row) => {
    const id = `${row.chain}:${row.address}`;
    setActionId(id);
    try {
      if (action === 'promote') {
        await api.post('/tokens/lifecycle/promote', { chain: row.chain, address: row.address });
      } else if (action === 'pin') {
        await api.post('/tokens/lifecycle/pin', { chain: row.chain, address: row.address, pinned: true });
      } else if (action === 'archive') {
        await api.post('/tokens/lifecycle/archive', { chain: row.chain, address: row.address });
      }
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setActionId(null);
    }
  };

  const byState = stats?.byState || {};

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Token Lifecycle</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Auto-tier junk to dormant → graveyard → archived. Recovery candidates stay immune. Re-cook promotes to active.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => runEval(true)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Dry-run eval
          </button>
          <button
            type="button"
            onClick={() => runEval(false)}
            className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Run evaluation
          </button>
          <button type="button" onClick={load} className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600">
            Refresh
          </button>
        </div>
      </div>

      {evalResult ? (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
          {evalResult.error
            ? evalResult.error
            : `Evaluated ${evalResult.evaluated} tokens — ${evalResult.changed} would change${evalResult.dryRun ? ' (dry run)' : ''}`}
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ['active', byState.active || 0, 'green'],
          ['dormant', byState.dormant || 0, 'yellow'],
          ['graveyard', byState.graveyard || 0, 'orange'],
          ['archived', byState.archived || 0, 'gray'],
          ['revivals', stats?.revivals || 0, 'blue'],
        ].map(([label, count, tone]) => (
          <div key={label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className={`text-2xl font-bold font-mono text-${tone}-600 dark:text-${tone}-400`}>{count}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATE_TABS.map((t) => (
          <button
            key={t.id || 'all'}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-full ${
              tab === t.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-500">
          {total} token(s)
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No tokens in this tier.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Token</th>
                  <th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Score</th>
                  <th className="px-4 py-2">MC / Liq</th>
                  <th className="px-4 py-2">Bad polls</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row) => {
                  const rowId = `${row.chain}:${row.address}`;
                  const busy = actionId === rowId;
                  return (
                    <tr key={rowId} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/token/${row.chain}/${row.address}`}
                          className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {row.symbol || '?'}
                        </Link>
                        <div className="text-xs text-gray-400 font-mono truncate max-w-[140px]">{row.address}</div>
                      </td>
                      <td className="px-4 py-3">{stateBadge(row.lifecycleState)}</td>
                      <td className="px-4 py-3 font-mono">{row.lifecycleScore ?? 0}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {fmtMc(row.currentMarketCap)} / {fmtMc(row.currentLiquidity)}
                      </td>
                      <td className="px-4 py-3 font-mono">{row.consecutiveBadPolls ?? 0}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[160px] truncate" title={row.lifecycleReason}>
                        {row.lifecycleReason || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.lifecycleState !== 'active' ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act('promote', row)}
                              className="px-2 py-1 text-xs rounded bg-green-600 text-white disabled:opacity-50"
                            >
                              Revive
                            </button>
                          ) : null}
                          {!row.lifecyclePinned ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act('pin', row)}
                              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            >
                              Pin
                            </button>
                          ) : (
                            <span className="text-xs text-blue-500">📌 pinned</span>
                          )}
                          {row.lifecycleState !== 'archived' ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act('archive', row)}
                              className="px-2 py-1 text-xs rounded bg-gray-600 text-white disabled:opacity-50"
                            >
                              Archive
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-xs text-gray-400">
        Worker evaluates hourly. Feed tiers: active (hot/warm) → dormant (30m) → graveyard (12h). Archived tokens drop off feed and schedulers.
      </p>
    </div>
  );
}
